import React from 'react';
import { useTranslation } from 'react-i18next';

interface AttachmentRendererProps {
    imageUrl?: string;
    attachmentUrl?: string;
    altText?: string;
}

const AttachmentRenderer: React.FC<AttachmentRendererProps> = ({
    imageUrl,
    attachmentUrl,
    altText
}) => {
    const { t } = useTranslation();

    // Simple file type detection based on extension
    const isAudioFile = (url: string): boolean => {
        const audioExtensions = ['.mp3', '.wav', '.ogg', '.m4a', '.aac', '.flac'];
        return audioExtensions.some(ext => url.toLowerCase().includes(ext));
    };

    const isImageFile = (url: string): boolean => {
        const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.svg', '.webp'];
        return imageExtensions.some(ext => url.toLowerCase().includes(ext));
    };

    return (
        <div>
            {/* Image attachment */}
            {imageUrl && (
                <div className="mb-4">
                    <img 
                        src={imageUrl}
                        alt={altText || t('evaluation.attachment.image')}
                        className="max-w-full h-auto rounded-lg border border-gray-200"
                    />
                </div>
            )}

            {/* File attachment */}
            {attachmentUrl && (
                <div className="mb-4">
                    {isImageFile(attachmentUrl) ? (
                        // Image file in attachment field
                        <img 
                            src={attachmentUrl}
                            alt={altText || t('evaluation.attachment.image')}
                            className="max-w-full h-auto rounded-lg border border-gray-200"
                        />
                    ) : isAudioFile(attachmentUrl) ? (
                        // Audio player
                        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex items-center gap-3 mb-2">
                                <svg className="w-5 h-5 text-theme-primary-text" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM15.657 6.343a1 1 0 011.414 0A9.972 9.972 0 0119 12a9.972 9.972 0 01-1.929 5.657 1 1 0 11-1.414-1.414A7.971 7.971 0 0017 12a7.971 7.971 0 00-1.343-4.243 1 1 0 010-1.414z" clipRule="evenodd" />
                                    <path fillRule="evenodd" d="M13.828 7.172a1 1 0 011.414 0A5.983 5.983 0 0117 12a5.983 5.983 0 01-1.758 4.828 1 1 0 11-1.414-1.414A3.987 3.987 0 0015 12a3.987 3.987 0 00-1.172-2.828 1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-medium text-gray-700">
                                    {t('evaluation.attachment.audioFile')}
                                </span>
                            </div>
                            <audio
                                key={attachmentUrl}
                                controls
                                className="w-full"
                                preload="metadata"
                            >
                                <source src={attachmentUrl} type="audio/mp4" />
                                <source src={attachmentUrl} type="audio/x-m4a" />
                                <source src={attachmentUrl} type="audio/mpeg" />
                                <source src={attachmentUrl} type="audio/wav" />
                                <source src={attachmentUrl} type="audio/ogg" />
                                {t('evaluation.attachment.audioNotSupported')}
                            </audio>
                        </div>
                    ) : (
                        // Generic file download
                        <a 
                            href={attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-theme-light text-theme-text hover:bg-theme-lightest"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                            </svg>
                            {t('evaluation.attachment.download')}
                        </a>
                    )}
                </div>
            )}
        </div>
    );
};

export default AttachmentRenderer;